<template>
  <div class="relative w-full" id="table-list-view">
    <svg
      width="100%"
      :viewBox="`0 0 ${columnCount} ${svgHeight}`"
      preserveAspectRatio="xMidYMid meet"
      id="tlv"
      ref="el"
    >
      <g v-if="!readOnly" class="axis">
        <g class="y-axis">
          <line
            v-for="tick in xTicks"
            :key="tick"
            :x1="tick"
            :y1="0"
            :x2="tick"
            :y2="svgHeight"
            :stroke-width="1 / ratio"
          />
        </g>

        <g class="x-axis">
          <line
            v-for="tick in yTicks"
            :key="tick"
            :x1="0"
            :y1="tick"
            :x2="columnCount"
            :y2="tick"
            :stroke-width="1 / ratio"
          />
        </g>
      </g>

      <!-- Render this doing our own math to force Safari to render it properly  -->
      <image
        v-if="backgroundImage"
        :href="backgroundImage"
        x="0"
        y="0"
        :width="width"
        :height="svgHeight * ratio"
        :transform="`scale(${1 / ratio})`"
      ></image>

      <g class="tables">
        <Shape
          v-for="(table, index) in tables"
          :key="index"
          :id="`table-${table.id}`"
          :useCheckmark="useCheckmark"
          :type="table.shape"
          :shapeData="table"
          :ratio="ratio"
          :radius="radius"
          :draggable="!readOnly"
          :orderedList="orderedList"
          :selectedTableOrder="selectedTables.indexOf(table.id) + 1"
          :selectable="selectable && !disabledTableIds.includes(table.id)"
          :selectedTablesView="selectedTablesView && selectedTables.includes(table.id)"
          :reservationTable="reservationView"
          :venueReservationTable="venueReservationView"
          :assetsDomain="assets_domain"
          :maxx="112"
          :maxy="svgHeight"
          @mouseover="showTooltip(table)"
          @mouseout="hideTooltip(table)"
          @update="$emit('updateCoordinates', $event)"
          @select="$emit('selectTable', index)"
        />
      </g>
    </svg>
    <TableListViewTooltip
      v-if="tooltipTable && tooltipTable.available && reservationView"
      :table="tooltipTable"
      :shapeElementCoords="shapeElementCoords"
      :parentElementCoords="parentElementCoords"
      :hideToolTip="hideToolTip"
    />
  </div>
</template>

<style scoped lang="scss">
svg {
  overflow: visible;
}

svg .x-axis line,
svg .y-axis line {
  stroke: rgb(223, 223, 223);
}
</style>

<script lang="ts">
import { useResizeObserver } from '@vueuse/core'
import { mapState, mapStores } from 'pinia'
import { PropType, defineComponent } from 'vue'

import Shape from '@/components/Shape.vue'
import TableListViewTooltip from '@/components/TableListViewTooltip.vue'
import { useSettingStore } from '@/stores/setting'

import { PerformanceTableRead, TableRead } from '@generated/types'

type AbstractTable = PerformanceTableRead | TableRead

export default defineComponent({
  components: {
    Shape,
    TableListViewTooltip,
  },
  props: {
    columnCount: {
      type: Number,
      required: false,
      default: 112,
    },
    disabledTableIds: {
      type: Array,
      required: false,
      default: () => [],
    },
    hideToolTip: {
      type: Boolean,
      required: false,
    },
    useCheckmark: {
      type: Boolean,
      required: false,
    },
    orderedList: {
      type: Boolean,
      required: false,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: true,
    },
    selectable: {
      type: Boolean,
      required: false,
      default: false,
    },
    tables: {
      type: Array as PropType<AbstractTable[]>,
      required: true,
    },
    selectedTables: {
      type: Array,
      required: true,
    },
    selectedTablesView: {
      type: Boolean,
      required: false,
      default: false,
    },
    radius: {
      type: Number,
      required: false,
      default: 2,
    },
    reservationView: {
      type: Boolean,
      required: false,
      default: false,
    },
    venueReservationView: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      tooltipTable: null as PerformanceTableRead | null,
      width: 500,
      shapeElementCoords: { top: 0, left: 0, width: 0 },
      parentElementCoords: { top: 0, left: 0, width: 0 },
    }
  },

  async mounted() {
    this.showTooltip(this.tables[0])
    useResizeObserver(this.topLevelRefsElement, this.onResize)
  },

  methods: {
    showTooltip(table: AbstractTable | null) {
      this.tooltipTable = table as PerformanceTableRead

      const tableListViewElement = document.getElementById('table-list-view') as HTMLElement
      const tableElement = document.getElementById(`table-${table?.id}`) as HTMLElement
      this.shapeElementCoords = {
        top: tableElement?.getBoundingClientRect().top,
        left: tableElement?.getBoundingClientRect().left,
        width: tableElement?.getBoundingClientRect().width,
      }
      this.parentElementCoords = {
        top: tableListViewElement?.getBoundingClientRect().top,
        left: tableListViewElement?.getBoundingClientRect().left,
        width: tableListViewElement?.getBoundingClientRect().width,
      }
    },

    hideTooltip(table: AbstractTable | null) {
      if (this.tooltipTable == table) {
        this.tooltipTable = null
      }
    },

    onResize() {
      try {
        this.width = this.topLevelRefsElement.getBoundingClientRect().width
      } catch {
        this.width = 500
      }
    },
  },
  computed: {
    ...mapStores(useSettingStore),
    ...mapState(useSettingStore, ['backgroundImage', 'assets_domain']),

    topLevelRefsElement(): SVGElement {
      return this.$refs.el as SVGElement
    },

    svgHeight(): number {
      return this.columnCount / this.settingStore.ratio
    },

    ratio(): number {
      return this.width / this.columnCount
    },

    xTicks(): number[] {
      const ticks: number[] = []
      for (let i = 2; i <= this.columnCount; i = i + 2) {
        ticks.push(i)
      }

      return ticks
    },

    yTicks(): number[] {
      const ticks: number[] = []
      for (let i = 2; i <= this.svgHeight; i = i + 2) {
        ticks.push(i)
      }

      return ticks
    },
  },
})
</script>
